import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/utils/api';
import { 
  Home, Sparkles, Star, Flame, Sun, ArrowRight, 
  MapPin, Search, Users, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const ICON_MAP = {
  Home, Sparkles, Star, Flame, Sun,
};

const CITIES = ['Hyderabad', 'Bangalore', 'Chennai'];

export default function ServicesLanding() {
  const [catalog, setCatalog] = useState([]);
  const [vendorCounts, setVendorCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get('/services/catalog?category=priest');
        setCatalog(res.data.catalog || []);
        const vRes = await apiClient.get('/services/vendors?category=priest');
        const counts = {};
        (vRes.data.vendors || []).forEach(v => {
          const city = v.location?.city || 'Unknown';
          counts[city] = (counts[city] || 0) + 1;
        });
        setVendorCounts(counts);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="services-landing">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        <div className="max-w-6xl mx-auto px-4 py-20 relative z-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur rounded-full text-sm font-medium mb-6 border border-white/20">
              ZenStudios Services Marketplace
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
              Find Trusted Service<br />Providers Near You
            </h1>
            <p className="text-lg text-purple-200 mb-8">
              Book verified priests, interior designers, and more — all in one place.
            </p>
            <Link to="/services/priest">
              <Button
                size="lg"
                className="bg-white text-purple-900 hover:bg-purple-50 font-semibold px-8 py-3 rounded-xl"
                data-testid="browse-priest-services-btn"
              >
                Browse Priest Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-lg font-semibold text-gray-900 mb-8 text-center">
          Available Services
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="h-40 bg-gray-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {catalog.map(item => {
              const IconComp = ICON_MAP[item.icon] || Star;
              return (
                <Link
                  key={item.catalog_id}
                  to={`/services/priest?service=${item.sub_category}`}
                  className="group bg-white rounded-xl border border-gray-100 p-6 hover:shadow-lg hover:border-purple-200 transition-all"
                  data-testid={`service-card-${item.sub_category}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
                      <IconComp className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{item.display_name}</h3>
                      <p className="text-sm text-gray-500 mb-3">{item.description}</p>
                      <div className="flex items-center gap-1 text-sm text-purple-600 font-medium">
                        Starting Rs. {item.suggested_price_range?.min?.toLocaleString()}
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Cities */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-8 text-center">
            Available Cities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {CITIES.map(city => (
              <Link
                key={city}
                to={`/services/priest?city=${city}`}
                className="group flex items-center gap-4 bg-gray-50 rounded-xl p-5 hover:bg-purple-50 transition-colors border border-gray-100 hover:border-purple-200"
                data-testid={`city-card-${city.toLowerCase()}`}
              >
                <div className="p-3 bg-white rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
                  <MapPin className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{city}</h3>
                  <p className="text-sm text-gray-500">
                    {vendorCounts[city] || 0} verified vendors
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          {[
            { icon: CheckCircle, label: 'Verified Vendors', desc: 'All partners are identity-verified' },
            { icon: Users, label: 'Matched for You', desc: 'Smart matching based on your location' },
            { icon: Star, label: 'Rated & Reviewed', desc: 'Transparent ratings from real customers' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex flex-col items-center">
              <div className="p-4 bg-purple-50 rounded-2xl mb-4">
                <Icon className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{label}</h3>
              <p className="text-sm text-gray-500">{desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
